import { Immutable } from "immer";
import React from "react";
import { SubBlockStarRating } from "tsi-common-react/src/components/SubBlockStarRating/index";
import { t } from "ttag";

import type {
    ServerCmsProductCardsProductCardData as IProductCardData,
    ServerCmsProductCardsProductCardPrice as IProductCardPrice,
} from "@reactivated";

import { richT } from "@thelabnyc/thelabui/src/utils/richText";

import { Template as CardFinancingModal } from "../../templates/CardFinancingModalTemplate";

/**
 * Renders the product card, scoped to the parameter subset the listing pages
 * pass (``product`` / ``class`` / ``auxCopy``; the ``feelNum`` / ``preTitle``
 * / ``imageOverride`` / ``imageSrcBG`` / ``reviewsOverride`` /
 * ``linkOverride`` branches were never exercised by them and are not
 * reproduced). Server-derived values (links, prices, translated copy, the
 * per-product JSON-LD, the rating/review-count) arrive via
 * ``server/cms/product_cards.py::build_product_card``, cached with the legacy
 * fragment cache's key parts and TTL.
 *
 * DOM notes:
 * - Keeps the per-card ``<script type="application/ld+json">`` INSIDE the
 *   card div and reproduces the double class emission when ``cssClass``
 *   repeats a base class.
 * - The reviews row renders whenever ``num_reviews > 0``
 *   (``reviews_href !== ""``); on tempurca ``SubBlockStarRating`` renders
 *   nothing but the ``<a>`` still renders.
 * - The financing-modal branch (``CardFinancingModalTemplate``) renders
 *   inline here, dropping the ``[data-include-react]`` host div (no CSS/JS
 *   anchors on it; accepted platform-wide divergence).
 */
type IProps = Immutable<{
    card: IProductCardData;
    cssClass: string;
    auxCopy?: boolean;
}>;

const PriceBlock = (
    props: Immutable<{ price: IProductCardPrice; cssClass: string }>,
): React.JSX.Element => {
    const { price, cssClass } = props;
    // Money/number values are the only interpolated placeholders; all prose
    // ("or", "/mo.", "for", "months", "(total payments of ...)") lives inside a
    // single t`` template so the whole sentence stays one translatable unit.
    // The underline wrappers and the <sup> footnote markers are markup tags
    // resolved by richT().
    const perMonth = price.installment_per_month;
    const termLength = price.installment_term_length;
    const total = price.installment_total;
    return (
        <div className={`product-card__price ${cssClass}__price`}>
            {!price.retail ? (
                <span>
                    <span className="product-card__price__actual">
                        {price.actual}
                    </span>
                </span>
            ) : (
                <span>
                    <span className="product-card__price__retail">
                        <span className="ada-screenreader-only">
                            {price.original_price_label}
                        </span>
                        {price.retail}
                    </span>
                    <span className="product-card__price__actual configurator__price__actual--highlight">
                        <span className="ada-screenreader-only">
                            {price.current_price_label}
                        </span>
                        {price.actual}
                    </span>
                </span>
            )}
            {price.installment_per_month && (
                <a
                    href={price.installment_href}
                    className={`product-card__installment ${cssClass}__installment`}
                >
                    {richT(
                        t`or <permonth>${perMonth}/mo.</permonth><sup>2</sup> for <term>${termLength} months</term><sup>1</sup> (total payments of ${total})`,
                        {
                            permonth: (children) => (
                                <span className="product-card__installment--underline">
                                    {children}
                                </span>
                            ),
                            term: (children) => (
                                <span className="product-card__installment--underline">
                                    {children}
                                </span>
                            ),
                        },
                    )}
                </a>
            )}
            {price.show_financing_modal && (
                <CardFinancingModal
                    price={price.financing_modal_price}
                    title={price.financing_modal_title}
                />
            )}
        </div>
    );
};

export const ProductCard = (props: IProps): React.JSX.Element => {
    const { card, cssClass } = props;
    return (
        <div
            className={`card product-card ${cssClass}`}
            data-product={card.product_id}
            role="group"
            aria-label={t`Product Card`}
        >
            {card.json_ld && (
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{ __html: card.json_ld }}
                />
            )}
            <a className="product-card__image-link" href={card.link}>
                <img
                    src={card.image_url}
                    className={`card__image ${cssClass}__image responsive-img`}
                    alt={card.image_alt}
                    loading="lazy"
                    decoding="async"
                />
            </a>
            <div className={`card__content ${cssClass}__content`}>
                <div
                    className={`card__title product-card__title ${cssClass}__title`}
                >
                    <h4>
                        <a
                            href={card.link}
                            dangerouslySetInnerHTML={{
                                __html: card.title_html,
                            }}
                        />
                    </h4>
                </div>
                {card.reviews_href && (
                    <a href={card.reviews_href} title={card.read_reviews_label}>
                        <SubBlockStarRating
                            rating={card.rating}
                            number={card.num_reviews}
                            className="product-card__rating"
                        />
                    </a>
                )}
                {props.auxCopy && (
                    <div
                        className={`product-card__aux-copy ${cssClass}__aux-copy`}
                        dangerouslySetInnerHTML={{
                            __html: card.description_html,
                        }}
                    />
                )}
                {card.price && (
                    <PriceBlock price={card.price} cssClass={cssClass} />
                )}
            </div>
        </div>
    );
};
